import React, { useState } from 'react';
import { 
  FaCreditCard, 
  FaPaypal, 
  FaGoogle, 
  FaApple, 
  FaLock, 
  FaShieldAlt, 
  FaCheckCircle,
  FaRegClock,
  FaTicketAlt,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUserFriends,
  FaArrowLeft
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import '../style.css';

const Payment = () => {
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    saveCard: false
  });
  const [upiId, setUpiId] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Booking details
  const bookingDetails = {
    tourName: "Goa Beach Paradise - 5 Days",
    bookingId: "VNS-2024-8765",
    date: "15-19 March 2024",
    travelers: 2,
    location: "Goa, India",
    basePrice: 24999,
    taxes: 2999,
    discount: 1500,
    totalAmount: 26498
  };

  const paymentMethods = [
    { id: 'card', name: 'Credit/Debit Card', icon: <FaCreditCard />, color: '#2d8cf0' },
    { id: 'upi', name: 'UPI', icon: <FaGoogle />, color: '#25d366' },
    { id: 'netbanking', name: 'Net Banking', icon: <FaShieldAlt />, color: '#ff6b6b' },
    { id: 'wallet', name: 'Wallet', icon: <FaPaypal />, color: '#3b7bbf' }
  ];

  const handleCardChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCardDetails(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCouponApply = () => {
    if (couponCode === 'VNSWELCOME' || couponCode === 'TRAVEL2024') {
      setCouponApplied(true);
      alert('Coupon applied successfully! ₹1500 discount applied.');
    } else {
      alert('Invalid coupon code. Try VNSWELCOME or TRAVEL2024');
    }
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsProcessing(false);
      setPaymentSuccess(true);
      
      // Reset after 5 seconds and redirect
      setTimeout(() => {
        navigate('/booking-confirmation');
      }, 5000);
    }, 2000);
  };

  const formatCardNumber = (value) => {
    return value.replace(/\D/g, '')
      .replace(/(\d{4})(?=\d)/g, '$1 ')
      .substr(0, 19);
  };

  const formatExpiryDate = (value) => {
    return value.replace(/\D/g, '')
      .replace(/(\d{2})(?=\d{2})/, '$1/')
      .substr(0, 5);
  };

  if (paymentSuccess) {
    return (
      <div className="payment-success-container">
        <div className="success-animation">
          <FaCheckCircle className="success-icon" />
          <h2>Payment Successful!</h2>
          <p>Your booking has been confirmed</p>
          <div className="booking-details-summary">
            <p><strong>Booking ID:</strong> {bookingDetails.bookingId}</p>
            <p><strong>Amount Paid:</strong> ₹{bookingDetails.totalAmount.toLocaleString()}</p>
            <p><strong>Tour:</strong> {bookingDetails.tourName}</p>
          </div>
          <p className="success-message">
            Booking confirmation and itinerary have been sent to your email.
          </p>
          <button 
            className="continue-btn"
            onClick={() => navigate('/my-bookings')}
          >
            View My Bookings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page">
      {/* Header */}
      <header className="payment-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <div className="header-content">
          <h1>Complete Your Booking</h1>
          <p>Secure payment • Instant confirmation • 24/7 support</p>
        </div>
        <div className="security-badge">
          <FaLock /> 100% Secure Payment
        </div>
      </header>

      <div className="payment-container">
        {/* Left Column - Booking Summary */}
        <div className="booking-summary">
          <div className="summary-header">
            <h2>Booking Summary</h2>
            <span className="booking-id">#{bookingDetails.bookingId}</span>
          </div>

          <div className="tour-details-card">
            <h3>{bookingDetails.tourName}</h3>
            <div className="tour-info-grid">
              <div className="info-item">
                <FaCalendarAlt className="info-icon" />
                <div>
                  <span className="info-label">Dates</span>
                  <span className="info-value">{bookingDetails.date}</span>
                </div>
              </div>
              <div className="info-item">
                <FaUserFriends className="info-icon" />
                <div>
                  <span className="info-label">Travelers</span>
                  <span className="info-value">{bookingDetails.travelers} Persons</span>
                </div>
              </div>
              <div className="info-item">
                <FaMapMarkerAlt className="info-icon" />
                <div>
                  <span className="info-label">Location</span>
                  <span className="info-value">{bookingDetails.location}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="price-breakdown">
            <h3>Price Details</h3>
            <div className="price-row">
              <span>Base Price (x{bookingDetails.travelers})</span>
              <span>₹{bookingDetails.basePrice.toLocaleString()}</span>
            </div>
            <div className="price-row">
              <span>Taxes & Fees</span>
              <span>₹{bookingDetails.taxes.toLocaleString()}</span>
            </div>
            {couponApplied && (
              <div className="price-row discount">
                <span>Discount (Coupon Applied)</span>
                <span>- ₹{bookingDetails.discount.toLocaleString()}</span>
              </div>
            )}
            <div className="price-row total">
              <span>Total Amount</span>
              <span className="total-amount">₹{bookingDetails.totalAmount.toLocaleString()}</span>
            </div>
          </div>

          {/* Coupon Section */}
          <div className="coupon-section">
            <div className="coupon-input-group">
              <FaTicketAlt className="coupon-icon" />
              <input
                type="text"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                className="coupon-input"
              />
              <button 
                className="apply-coupon-btn"
                onClick={handleCouponApply}
                disabled={couponApplied}
              >
                {couponApplied ? 'Applied' : 'Apply'}
              </button>
            </div>
            <p className="coupon-note">Try: VNSWELCOME or TRAVEL2024</p>
          </div>

          {/* Security Features */}
          <div className="security-features">
            <h3>Secure Payment Guaranteed</h3>
            <div className="features-grid">
              <div className="feature">
                <FaLock />
                <span>256-bit SSL Encryption</span>
              </div>
              <div className="feature">
                <FaShieldAlt />
                <span>PCI DSS Compliant</span>
              </div>
              <div className="feature">
                <FaRegClock />
                <span>Instant Refund Policy</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Payment Form */}
        <div className="payment-form-section">
          {/* Payment Method Selection */}
          <div className="payment-methods">
            <h2>Select Payment Method</h2>
            <div className="method-grid">
              {paymentMethods.map(method => (
                <div
                  key={method.id}
                  className={`method-card ${paymentMethod === method.id ? 'selected' : ''}`}
                  onClick={() => setPaymentMethod(method.id)}
                  style={{ borderColor: paymentMethod === method.id ? method.color : '#ddd' }}
                >
                  <div className="method-icon" style={{ color: method.color }}>
                    {method.icon}
                  </div>
                  <span>{method.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Card Payment Form */}
          {paymentMethod === 'card' && (
            <div className="card-form">
              <h3>Card Details</h3>
              <div className="form-group">
                <label>Card Number</label>
                <div className="input-with-icon">
                  <FaCreditCard className="input-icon" />
                  <input
                    type="text"
                    name="cardNumber"
                    value={cardDetails.cardNumber}
                    onChange={(e) => setCardDetails(prev => ({
                      ...prev,
                      cardNumber: formatCardNumber(e.target.value)
                    }))}
                    placeholder="1234 5678 9012 3456"
                    maxLength="19"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Name on Card</label>
                <input
                  type="text"
                  name="cardName"
                  value={cardDetails.cardName}
                  onChange={handleCardChange}
                  placeholder="John Doe"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Expiry Date</label>
                  <input
                    type="text"
                    name="expiryDate"
                    value={cardDetails.expiryDate}
                    onChange={(e) => setCardDetails(prev => ({
                      ...prev,
                      expiryDate: formatExpiryDate(e.target.value)
                    }))}
                    placeholder="MM/YY"
                    maxLength="5"
                  />
                </div>
                <div className="form-group">
                  <label>CVV</label>
                  <div className="input-with-icon">
                    <FaLock className="input-icon" />
                    <input
                      type="password"
                      name="cvv"
                      value={cardDetails.cvv}
                      onChange={(e) => setCardDetails(prev => ({
                        ...prev,
                        cvv: e.target.value.replace(/\D/g, '').substr(0, 3)
                      }))}
                      placeholder="123"
                      maxLength="3"
                    />
                  </div>
                </div>
              </div>

              <div className="form-checkbox">
                <input
                  type="checkbox"
                  id="saveCard"
                  name="saveCard"
                  checked={cardDetails.saveCard}
                  onChange={handleCardChange}
                />
                <label htmlFor="saveCard">Save this card for future payments</label>
              </div>
            </div>
          )}

          {/* UPI Payment Form */}
          {paymentMethod === 'upi' && (
            <div className="upi-form">
              <h3>UPI Payment</h3>
              <div className="form-group">
                <label>UPI ID</label>
                <input
                  type="text"
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  placeholder="username@bank"
                />
              </div>
              <p className="upi-note">
                After clicking "Pay Now", you will be redirected to your UPI app for payment confirmation.
              </p>
            </div>
          )}

          {/* Terms and Conditions */}
          <div className="terms-section">
            <div className="form-checkbox">
              <input type="checkbox" id="terms" defaultChecked />
              <label htmlFor="terms">
                I agree to the <a href="/terms">Terms & Conditions</a> and 
                <a href="/privacy"> Privacy Policy</a>
              </label>
            </div>
            <div className="form-checkbox">
              <input type="checkbox" id="cancellation" defaultChecked />
              <label htmlFor="cancellation">
                I understand the <a href="/cancellation">cancellation policy</a>
              </label>
            </div>
          </div>

          {/* Pay Button */}
          <button 
            className={`pay-now-btn ${isProcessing ? 'processing' : ''}`}
            onClick={handlePayment}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <div className="spinner"></div>
                Processing Payment...
              </>
            ) : (
              `Pay Now - ₹${bookingDetails.totalAmount.toLocaleString()}`
            )}
          </button>

          {/* Payment Partners */}
          <div className="payment-partners">
            <p>Accepted Payment Methods</p>
            <div className="partners-grid">
              <div className="partner visa">VISA</div>
              <div className="partner mastercard">MasterCard</div>
              <div className="partner razorpay">Razorpay</div>
              <div className="partner paytm">Paytm</div>
              <div className="partner googlepay">Google Pay</div>
              <div className="partner phonepe">PhonePe</div>
            </div>
          </div>

          {/* Support Info */}
          <div className="support-info">
            <h4>Need Help?</h4>
            <p>Call our 24/7 support: <strong>+91 6307291567</strong></p>
            <p>Email: <strong>support@vnstour.com</strong></p>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="payment-footer">
        <p>
          <FaLock /> Your payment is secured with 256-bit SSL encryption. 
          We do not store your card details.
        </p>
      </div>
    </div>
  );
};

export default Payment;